import { moment } from 'localization'
import { pascalToDash } from 'utils'

import { dateTimeFormat, i18n } from 'localization'
import { parseDate, parsePerson, translateTaskType } from './utils'

const priorities = {
	1: 'very-high',
	2: 'high',
	3: 'medium',
	4: 'low',
	5: 'very-low',
}
export default class Task {
	static fromJSON(json) {
		return (
			new Task(
				json.id,
				json.taskType,
				json.process?.id || json.metadata?.processId,
				json.finalSituationPrevisionMetadata ||
					json.requisitionMetadata ||
					json.opinionMetadata ||
					json.ticketMetadata,
				json.requisitionType,
				json.process?.info
			)
				// .withEntity(json.entityId, json.entityType)
				.withRequisitionMetadata(json.requisitionMetadata)
				.withInjured(json.process?.injured)
				.withInsuranceCompany(
					(json.process || json.metadata)?.insuranceCompany
				)
				.withInsurancePolicy(
					json.process?.insurancePolicy,
					json.process?.temporaryProcessId
				)
				// .withUserId(json.userId)
				.withUser(json.user)
				.withSegment((json.process || json.metadata)?.segment)
				.withProvider((json.process || json.metadata)?.providerEntity)
				.withMainMedicalDiagnosis(
					(json.process || json.metadata)?.mainMedicalDiagnosis
				)
				.withOpinionRequest(json?.opinionMetadata)
				.withProcessRequest(json?.processRequestInfo)
				.withRequiresChargeAmount(json.process?.requiresChargeAmount)
				.withRequiringUser(json.requiringAuthor)
				.withTimestamps(json.createdAt)
				.withDueDate(json.postponedStateMetadata)
				.withStatus(json.state)
				.withProcessInjuredStateId(json.process?.processInjuredStateId)
				.withPriority(json.priority)
				.withNote(json.note)
				.withRequisitionType(json.requisitionType)
				.withAccident(json?.process?.accident)
				.withAdmissionDate(json?.process?.info?.admissionDate)
				.withObservations(json.process?.observations)
				.withForwardUrgency(json.process?.forwardUrgencyId)
				.withAppointmentId(json.appointmentId)
				.withRequisitionState(json?.requisitionState)
				.withFormState(json?.formState)
				.withLink(json)
				.withTicketMetadata(json.ticketMetadata)
		)
	}

	constructor(id, type, processId, metadata, requisitionType, info) {
		this.id = id
		this.processId = processId?.toString()
		// TYPE is in Pascal Case
		if (type) {
			this.type = type
			this.subType = getSubType(type, metadata, info)

			this.name = i18n.translate(
				translateTaskType(this.subType || this.type)
			)
			this.title = getTitle(type, requisitionType, this.name)
			this.icon = pascalToDash(type === 'Ticket' ? this.subType : type)
		}
	}

	withRequisitionMetadata(requisitionMetadata) {
		this.requisitionId = requisitionMetadata?.requestId
		return this
	}

	withInjured(json) {
		const injured = parsePerson(json)
		this.injured = {
			...injured,
			postalCode: json?.postalCode || '',
		}
		return this
	}

	withInsuranceCompany(json) {
		this.insuranceCompany = json
			? {
					id: json.id,
					name: json.name,
					shortName: json.shortName,
			  }
			: null
		return this
	}

	withInsurancePolicy(insurancePolicy, tempId) {
		this.insurancePolicy = insurancePolicy
			? {
					policyNumber: insurancePolicy.policyNumber,
					externalId: insurancePolicy.externalId,
					tempId: tempId,
			  }
			: null
		return this
	}

	withProvider(json) {
		this.provider = json
			? {
					id: json.id,
					name: json.name,
			  }
			: null
		return this
	}

	withMainMedicalDiagnosis(json) {
		this.mainMedicalDiagnosis = json
			? {
					icd9Code: json.icd9Code,
					icd9Diagnostic: json.icd9Diagnostic,
					bodyPartName: json.bodyPartName,
					bodyPartId: json.bodyPartId,
			  }
			: null
		return this
	}

	withOpinionRequest(json) {
		this.opinionRequest =
			json?.author || json?.description || json?.opinionId
				? {
						author: json?.author,
						description: json?.description,
						opinionId: json?.opinionId,
				  }
				: null
		return this
	}

	withProcessRequest(processRequest) {
		this.processRequest = processRequest

		return this
	}

	withUserId(userId) {
		this.userId = userId

		return this
	}

	withUser(user) {
		this.user = user

		return this
	}

	withSegment(json) {
		this.segment = json
			? {
					id: json.id,
					abbreviation: json.id,
					name: json.name,
			  }
			: null
		return this
	}

	withTimestamps(createAt) {
		this.createAtRaw = moment(
			new Date(
				(createAt?.seconds?.low ?? createAt?.seconds) * 1000 +
					(createAt?.nanos || 0) / 1000
			)
		)

		this.createdAt = this.createAtRaw.format(
			dateTimeFormat.date || 'MMM DD, YYYY'
		)
		return this
	}

	withRequiresChargeAmount(requiresChargeAmount) {
		this.requiresChargeAmount = requiresChargeAmount
		return this
	}

	withRequiringUser(requiringAuthor) {
		this.requiringUser = requiringAuthor ? requiringAuthor : null
		return this
	}

	withDueDate(postponedMetadata) {
		let dateTime
		if (postponedMetadata?.dueDate) {
			const dueDate = postponedMetadata.dueDate
			dateTime = moment(
				new Date(
					(dueDate.seconds?.low ?? dueDate?.seconds) * 1000 +
						(dueDate.nanos || 0) / 1000
				)
			)
		} else {
			dateTime = this.createAtRaw.add(1, 'days')
		}

		const date = dateTime.format(dateTimeFormat.date || 'MMM DD, YYYY')
		const time = dateTime.format(dateTimeFormat.date || 'HH:mm')
		const dateTimeString = dateTime.format(
			dateTimeFormat.dateTime || 'MMM DD, YYYY [at] HH:mm'
		)
		const fromNow = dateTime.fromNow()
		const ms = moment(dateTime).diff()
		const distance = moment.duration(ms).asHours()
		const dueStatus =
			distance < 2 ? 'burning' : distance < 4 ? 'near' : 'on-time'
		this.dueDate = {
			timestamp: dateTime.valueOf(),
			shortDateFormat: date,
			shortTimeFormat: time,
			dateTimeFormat: dateTimeString,
			fromNow,
			distance,
			dueStatus,
		}

		return this
	}

	withStatus(status) {
		this.status = status
		this.isComplete = status === 'Completed'
		return this
	}

	withProcessInjuredStateId(id) {
		this.processInjuredStateId = id

		return this
	}

	withPriority(priority) {
		this.priority = { id: priority, name: priorities[priority] }

		return this
	}

	withNote(note) {
		this.note = note

		return this
	}

	withRequisitionType(requisitionType) {
		this.requisitionType = requisitionType
		return this
	}

	withAccident(json) {
		this.accident = {
			date: parseDate(json?.date || ''),
			local: json?.local,
			description: json?.description,
		}
		return this
	}

	withLink(json) {
		if (json?.process?.id) {
			this.link = {
				to: `/clinical-processes/${json?.process?.id}/appointments?taskId=${json.id}`,
				name: i18n`See clinical process`,
			}
		}
		return this
	}

	withAdmissionDate(json) {
		this.admissionDate = parseDate(json)
		return this
	}

	withObservations(observations) {
		this.observations = observations
		return this
	}

	withForwardUrgency(forwardUrgencyId) {
		this.forwardUrgencyId = forwardUrgencyId
		return this
	}

	withAppointmentId(appointmentId) {
		this.appointmentId = appointmentId
		return this
	}

	withRequisitionState(requisitionState) {
		this.requisitionState = requisitionState
		return this
	}

	withFormState(formState) {
		this.formState = formState ? JSON.parse(formState) : null
		return this
	}

	withTicketMetadata(ticketMetadata) {
		this.ticketMetadata = ticketMetadata ? ticketMetadata : null
		return this
	}
}

/*CM 194 */
const getSubType = (type, metadata, info) => {
	switch (type) {
		case 'FinalSituationPrevision':
			return metadata?.manualRequest
				? 'ManualIPP'
				: metadata?.firstFinalSituationPrevision
				? 'FirstIPP'
				: 'FollowUpIPP'
		case 'TriageOpinion':
			return 'FirstTriageOpinion'
		case 'TriageOpinionFollowUp':
			return 'FollowUpTriageOpinion'
		case 'Ticket': {
			switch (metadata.ticketType) {
				// This result is used for the icon ContactClaimant uses the contact-claimant symbol from liita-ui
				case 3:
					return 'Transports'
				case 4:
					return 'ContactClaimant'
				case 5:
					return 'ContactProvider'
				case 6:
					return 'Clarification'
				case 7:
					return 'ClinicalClarification'
				case 8:
					return 'TechnicalAssistance'

				default:
					return 'Ticket'
			}
		}
		default:
			return null
	}
}

const getTitle = (type, requisitionType, name) => {
	switch (type) {
		case 'MedicalActsClinicalApproval':
			return i18n`${requisitionType?.name} Approval`
		default:
			return name
	}
}
